import React, { useState, useCallback } from 'react';
import cls from 'clsx';
import { Search } from '@linktivity/link-icons';
import { Button } from '@linktivity/link-ui';
import { SearchBarProps } from './SearchBar.types';
import styles from './searchBar.module.css';

const SearchBar: React.FC<SearchBarProps> = ({
  variant = 'icon-button',
  searchLabel,
  placeholder = '',
  value: controlledValue,
  onChange,
  onSearch,
  className,
  containerClassName
}) => {
  const [internalValue, setInternalValue] = useState('');
  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : internalValue;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (!isControlled) setInternalValue(newValue);
      onChange?.(newValue);
    },
    [isControlled, onChange]
  );

  const handleSearch = useCallback(() => {
    onSearch?.(value);
  }, [onSearch, value]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') handleSearch();
    },
    [handleSearch]
  );

  return (
    <div className={cls(styles.wrapper, className)}>
      <div
        className={cls(
          styles.container,
          variant === 'text-button' && styles.textButton,
          containerClassName
        )}
      >
        {variant === 'text-button' && <Search className={styles.searchIcon} />}
        <input
          type="text"
          className={styles.input}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
        {variant === 'text-button' ? (
          <Button
            variant="filled"
            className={styles.searchButtonText}
            onClick={handleSearch}
            aria-label={placeholder}
          >
            {searchLabel}
          </Button>
        ) : (
          <button
            type="button"
            className={styles.searchButton}
            onClick={handleSearch}
            aria-label={placeholder}
          >
            <Search className={styles.searchIcon} />
          </button>
        )}
      </div>
    </div>
  );
};

export default SearchBar;
